import React from "react";
import { Check, Lock, UserPlus } from "lucide-react";
import type { PeopleAccessTag } from "@playbookai/types";
import { Badge, type BadgeTone } from "../../atoms/Badge/index.js";
import { TONES } from "../../lib/tones.js";
import { STATE_RING } from "../../lib/state-ring.js";
import { cn } from "../../lib/utils.js";
import type { PeopleFieldResolvedLabels } from "./people-field-labels.js";
import type {
  PeopleFieldItem,
  PeopleFieldSubLine,
} from "./people-field-model.js";
import { PeopleFieldAvatar } from "./PeopleFieldAvatar.js";
import { PeopleFieldLoadMoreRow } from "./PeopleFieldLoadMoreRow.js";
import { PeopleFieldMatchedName } from "./PeopleFieldMatchedName.js";

/* One option of the open list. states-check-ignore: rows are never focused —
   focus stays on the search input and the active row is painted with
   pb-overlay-paint through aria-activedescendant (ComboboxListbox's rule). */

/* Owner and Facilitator stand out as the roster's own pills do (RosterRow);
   every other role is quiet. */
const BADGE_TONE: Record<PeopleAccessTag["tone"], BadgeTone> = {
  owner: "watch",
  facilitator: "info",
  neutral: "neutral",
};

/* A grant still to happen reads as information; a fact or a refusal is
   muted, and a refusal carries a lock so colour is never the only signal. */
const SUB_TEXT: Record<PeopleFieldSubLine["tone"], string> = {
  grant: TONES.info.text,
  muted: TONES.neutral.text,
  locked: TONES.neutral.text,
};

export interface PeopleFieldRowProps {
  readonly item: PeopleFieldItem;
  /** The option's id, which the search input's `aria-activedescendant` names. */
  readonly id: string;
  readonly active: boolean;
  readonly query: string;
  readonly labels: PeopleFieldResolvedLabels;
  readonly loadingMore: boolean;
  readonly loadMoreFailed: boolean;
  readonly onHover: () => void;
  readonly onPick: () => void;
}

export function PeopleFieldRow({
  item,
  id,
  active,
  query,
  labels,
  loadingMore,
  loadMoreFailed,
  onHover,
  onPick,
}: PeopleFieldRowProps): React.ReactElement {
  const person = item.kind === "person" ? item : null;
  const disabled = person?.disabled ?? false;
  const email = person?.person.email ?? null;
  const ids = {
    name: `${id}-name`,
    detail: `${id}-detail`,
    sub: `${id}-sub`,
    badge: `${id}-badge`,
  };
  const sub =
    item.kind === "typed"
      ? { text: labels.typedDetail, tone: "muted" as const }
      : (person?.sub ?? null);
  const detail = item.kind === "me" ? item.detail : email;
  const badge = person?.badge ?? null;
  const describedBy = [
    detail ? ids.detail : null,
    sub ? ids.sub : null,
    badge ? ids.badge : null,
  ]
    .filter(Boolean)
    .join(" ");

  if (item.kind === "loadMore") {
    return (
      <PeopleFieldLoadMoreRow
        id={id}
        active={active}
        labels={labels}
        loadingMore={loadingMore}
        loadMoreFailed={loadMoreFailed}
        onHover={onHover}
        onPick={onPick}
      />
    );
  }

  const name =
    item.kind === "me" ? (
      labels.assignToMe
    ) : item.kind === "typed" ? (
      labels.typedName(item.text)
    ) : (
      <>
        <PeopleFieldMatchedName name={item.person.name} query={query} />
        {item.isCurrentUser ? (
          <span className="font-normal text-pb-text-muted"> {labels.you}</span>
        ) : null}
      </>
    );
  const avatar =
    item.kind === "typed" ? (
      <PeopleFieldAvatar kind="typed" seed={undefined} name={item.text} />
    ) : (
      <PeopleFieldAvatar
        kind="seed"
        seed={item.person.id}
        name={item.person.name}
      />
    );

  return (
    <div
      id={id}
      role="option"
      tabIndex={-1}
      aria-selected={person?.selected ?? false}
      aria-disabled={disabled || undefined}
      aria-labelledby={ids.name}
      aria-describedby={describedBy || undefined}
      onMouseEnter={onHover}
      onMouseDown={(event) => {
        event.preventDefault();
        onPick();
      }}
      className={cn(
        "flex min-h-11 items-center gap-2.5 rounded-md px-2 py-1.5",
        disabled ? "cursor-not-allowed" : "cursor-pointer",
        // The overlay is 0% in high contrast; the ring shows it (d16).
        active && `pb-overlay-paint ${STATE_RING.neutral}`,
      )}
    >
      <span className={cn("flex shrink-0", disabled && "opacity-60")}>
        {avatar}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          id={ids.name}
          className={cn(
            "truncate text-sm font-medium text-pb-text",
            disabled && "opacity-60",
          )}
        >
          {name}
        </span>
        {detail ? (
          <span
            id={ids.detail}
            className={cn(
              "truncate text-caption text-pb-text-muted",
              disabled && "opacity-60",
            )}
          >
            {detail}
          </span>
        ) : null}
        {sub ? (
          <span
            id={ids.sub}
            className={cn(
              "mt-0.5 flex items-center gap-1 text-caption",
              SUB_TEXT[sub.tone],
            )}
          >
            {sub.tone === "grant" ? (
              <UserPlus aria-hidden="true" className="h-3 w-3 shrink-0" />
            ) : sub.tone === "locked" ? (
              <Lock aria-hidden="true" className="h-3 w-3 shrink-0" />
            ) : null}
            <span className="min-w-0 truncate">{sub.text}</span>
          </span>
        ) : null}
      </span>
      {badge ? (
        <Badge
          id={ids.badge}
          variant="outline"
          shape="pill"
          size="sm"
          tone={BADGE_TONE[badge.tone]}
          className="shrink-0"
        >
          {badge.label}
        </Badge>
      ) : null}
      {person?.selected ? (
        <Check
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-pb-teal-text"
        />
      ) : null}
    </div>
  );
}
